
import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { catchError, debounceTime, map, merge, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { DistrictDto } from '../../../../generated-model/models';
import { DistrictService } from '../../../views/master/district/district-service';

/** Page size asked of the service, and the hard cap on how many suggestions are shown. */
const SUGGESTION_LIMIT = 10;
const DEBOUNCE_MS = 300;

@Component({
  selector: 'app-district-autocomplete',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './district-autocomplete.html',
  styleUrl: './district-autocomplete.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DistrictAutocompleteComponent),
      multi: true,
    },
  ],
})
export class DistrictAutocompleteComponent implements ControlValueAccessor, OnChanges {
  @Input() placeholder = 'พิมพ์ค้นหารหัสหรือชื่ออำเภอ';
  @Input() initialDisplayText: string | null = null;
  @Input() bindValue: 'id' | 'code' | null = 'id';
  @Output() selected = new EventEmitter<DistrictDto>();

  @ViewChild('searchInput', { static: true }) searchInput?: ElementRef<HTMLInputElement>;

  searchText = '';
  suggestions: DistrictDto[] = [];
  loading = false;
  open = false;
  disabled = false;

  private selectedValue: DistrictDto | number | string | null = null;
  private onChange: (value: DistrictDto | number | string | null) => void = () => {};
  private onTouched: () => void = () => {};

  /** Keystrokes — debounced so one request is sent per pause, not per character. */
  private readonly typed$ = new Subject<string>();
  /** Dropdown opened by focus/caret — searched right away. */
  private readonly requested$ = new Subject<string>();

  constructor(private districtService: DistrictService) {
    merge(this.typed$.pipe(debounceTime(DEBOUNCE_MS)), this.requested$)
      .pipe(
        tap(() => (this.loading = true)),
        // switchMap drops the response of a superseded term, so a slow early
        // request can never overwrite the suggestions of a later one.
        switchMap((term) => this.fetchSuggestions(term)),
        takeUntilDestroyed(),
      )
      .subscribe((districts) => {
        this.suggestions = districts;
        this.open = districts.length > 0;
        this.loading = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialDisplayText'] && this.selectedValue != null && this.initialDisplayText) {
      this.searchText = this.initialDisplayText;
    }
  }

  writeValue(value: number | string | DistrictDto | null): void {
    this.selectedValue = value;
    if (value == null) {
      this.searchText = '';
      this.suggestions = [];
      return;
    }

    if (typeof value === 'object') {
      this.searchText = this.formatDistrict(value);
      return;
    }

    if (this.initialDisplayText) {
      this.searchText = this.initialDisplayText;
      return;
    }

    this.searchText = `${value}`;
    if (this.bindValue === 'id' && typeof value === 'number') {
      this.districtService.getDistrictById(value).subscribe({
        next: (district) => {
          this.searchText = this.formatDistrict(district);
        },
        error: () => {
          this.searchText = `${value}`;
        },
      });
    }
  }

  registerOnChange(fn: (value: DistrictDto | number | string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.open && !target.closest('.district-autocomplete')) {
      this.open = false;
      this.onTouched();
    }
  }

  onInputChange(): void {
    const term = this.searchText.trim();
    this.selectedValue = null;
    this.onChange(null);

    if (!term) {
      this.suggestions = [];
      this.open = false;
      this.loading = false;
      return;
    }

    this.typed$.next(term);
  }

  onFocus(): void {
    // Show the first page of districts when focusing an empty input.
    if (!this.searchText.trim() && !this.open) {
      this.requested$.next('');
    }
  }

  toggleOpen(): void {
    if (this.disabled) {
      return;
    }
    if (this.open) {
      this.open = false;
      return;
    }
    this.requested$.next(this.searchText.trim());
  }

  selectDistrict(item: DistrictDto): void {
    this.selectedValue = item;
    this.searchText = this.formatDistrict(item);
    this.suggestions = [];
    this.open = false;
    this.selected.emit(item);

    if (this.bindValue === null) {
      this.onChange(item);
    } else if (this.bindValue === 'id') {
      this.onChange(item.id);
    } else {
      this.onChange(item.code ?? null);
    }
    this.onTouched();
  }

  handleBlur(): void {
    // Closing is owned by onClickOutside/selectDistrict; a blur timeout here would
    // race the caret click and immediately re-close the dropdown it just opened.
  }

  trackByDistrictId(_: number, item: DistrictDto): number {
    return item.id;
  }

  private fetchSuggestions(term: string): Observable<DistrictDto[]> {
    return this.districtService.getAllDistricts(0, SUGGESTION_LIMIT, term).pipe(
      map((page) => {
        const list = page?.content ?? [];
        return term ? list : list.slice(0, SUGGESTION_LIMIT);
      }),
      catchError(() => of([])),
    );
  }

  private formatDistrict(item: DistrictDto): string {
    return `${item.code ?? '-'} - ${item.title ?? ''}`.trim();
  }
}
